import { strapiClient } from "@shared/api";
import rebuild from "@shared/api/rebuild";

import { mapDtoToAccommodationsPoints } from "../model";
import type { AccommodationsPoints } from "../model/types";

const parseCombinedFilter = (filter: string) => {
  const parts = (filter || "all")
    .split("&")
    .map((p) => p.trim())
    .filter(Boolean);

  const badgesPart = parts.find((p) => p.startsWith("badges="));
  const badges = badgesPart
    ? badgesPart
        .replace("badges=", "")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean)
    : [];

  const badgesExcludePart = parts.find((p) => p.startsWith("badges_exclude="));

  const badgesExclude = badgesExcludePart
    ? badgesExcludePart
        .replace("badges_exclude=", "")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean)
    : [];

  const tabUid =
    parts.find(
      (p) => !p.startsWith("badges=") && !p.startsWith("badges_exclude="),
    ) || "all";

  return { tabUid, badges, badgesExclude };
};

export const getCurrentAccommodationsPoint = async (
  accommodationsType: string,
  pageCount: number,
) => {
  const { tabUid, badges, badgesExclude } =
    parseCombinedFilter(accommodationsType);

  const andFilters: Array<Record<string, unknown>> = [];

  if (tabUid !== "all") {
    andFilters.push({
      accommodations_type: {
        uid: {
          $in: [tabUid],
        },
      },
    });
  }

  // New logic: include points that have ANY selected badge.
  if (badges.length > 0) {
    andFilters.push({
      accommodations_badge_types: {
        uid: {
          $in: badges,
        },
      },
    });
  } else if (badgesExclude.length > 0) {
    // Backward compatibility: exclude points that have ANY excluded badge.
    // This also keeps points with no badges in the relation.
    andFilters.push({
      $not: {
        accommodations_badge_types: {
          uid: {
            $in: badgesExclude,
          },
        },
      },
    });
  }

  const filters = andFilters.length > 0 ? { $and: andFilters } : {};

  const searchParams = {
    sort: ["createdAt:desc"],
    pagination: {
      page: pageCount || 1,
      pageSize: 6,
    },
    filters: { ...filters },
    populate: {
      photos: {
        populate: "*",
      },
      accommodations_type: {
        populate: "*",
      },
      accommodations_badge_types: {
        populate: "*",
      },
      workTimeList: {
        populate: "*",
      },
    },
  };

  const accommodationsPoints = strapiClient.collection("accommodations-points");

  const points = await accommodationsPoints.find({
    ...searchParams,
  });

  return rebuild(mapDtoToAccommodationsPoints(points)) as AccommodationsPoints;
};
